/**
 * Feature access of the signed-in user, as GET /api/me/access returns it.
 * The server works out `features` (Super Admin and Full-Access included), so
 * the rule lives in one place: apps/api/src/auth/features.ts.
 */
export type Feature = "dooh" | "seo" | "projects";

export interface MyAccess {
	siteRole: string;
	fullAccess: boolean;
	/** The personal space. */
	features: Feature[];
	/** One list per team the user is in (access is separate per space). */
	teams: Record<string, Feature[]>;
}

/** The page a user lands on after login: the first one they can open. */
export function homePathFor(features: readonly Feature[]): string {
	if (features.includes("projects")) return "/dashboard";
	if (features.includes("seo")) return "/analyzer";
	if (features.includes("dooh")) return "/out-of-home";
	return "/settings";
}

// Manager page (Super Admin): the shapes /api/admin returns and accepts.
export const FEATURES: readonly Feature[] = ["dooh", "seo", "projects"];
/** DOOH has no team space, so a team's access offers only these. */
export const TEAM_FEATURES: readonly Feature[] = ["seo", "projects"];
export type SiteRole = "super_admin" | "admin" | "user";
export type AssignableTeamRole = "manager" | "member" | "viewer";

export interface AdminUser {
	id: string;
	name: string;
	email: string;
	siteRole: SiteRole;
	isActive: boolean;
	fullAccess: boolean;
	featureAccess: Feature[];
	createdAt: string;
	teams: {
		teamId: string;
		teamName: string;
		role: AssignableTeamRole | "owner";
		/** Access inside this team, separate from the personal access above. */
		fullAccess: boolean;
		featureAccess: Feature[];
	}[];
}

export interface NewAdminUser {
	name: string;
	email: string;
	password: string;
	siteRole: SiteRole;
	fullAccess: boolean;
	featureAccess: Feature[];
	team?: { teamId: string; role: AssignableTeamRole; fullAccess: boolean; featureAccess: Feature[] };
}

/**
 * What counts in the space chosen in the switcher (`teamId` null = personal).
 * DOOH has no team space, so it always comes from the personal access.
 */
export function spaceFeatures(access: MyAccess | undefined, teamId: string | null) {
	const personal = access?.features ?? [];
	const inSpace = teamId ? (access?.teams?.[teamId] ?? []) : personal;
	const current: Feature[] = [
		...inSpace.filter((feature) => feature !== "dooh"),
		...(personal.includes("dooh") ? (["dooh"] as Feature[]) : []),
	];
	const anySpace = new Set<Feature>([...personal, ...Object.values(access?.teams ?? {}).flat()]);
	return { current, anySpace };
}
